/**
 * AI剪辑第三方 JSON（record.json / warnings.json）渲染前的归一化工具。
 *
 * 两份负载都来自 KataBit（外部剪辑代理 + 人工评论），字段类型不受本仓约束。
 * 本仓没有 ErrorBoundary（src/index.tsx 直接 createRoot），任一渲染抛错
 * 会卸载整棵 root → 整个剧集详情页白屏，所以所有不可信值必须先过这里。
 */

/** 不可信数组 → 只保留「非 null 且非数组」的对象元素；非数组输入返回空数组。 */
export function objectItems<T>(value: unknown): T[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    item => typeof item === 'object' && item !== null && !Array.isArray(item),
  ) as T[];
}

/**
 * 不可信数值是否可直接参与展示计算（toFixed / 乘法 / 拼接）。
 *
 * JSON 里不会出现字面量 NaN/Infinity，但第三方可能把 `1e999` 这类值写进负载
 * （JSON.parse 得到 Infinity），或让上游算出 NaN；不做有限性检查就会渲染出
 * 「成片 NaNs」「转写对齐 NaN%」这类假数据。textOf 与所有 typeof === 'number'
 * 判断统一走这里，避免两套口径。
 */
export const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/** 不可信标量 → 可安全进 JSX 的字符串；非 string/有限 number 一律丢弃。 */
export const textOf = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (isFiniteNumber(value)) return String(value);
  return '';
};

/**
 * 不可信值 → 缩进 JSON 文本；序列化失败时回落到调用方给的占位文案。
 *
 * 必须有 try/catch：V8 的 JSON.parse 是迭代实现（20 万层嵌套都不抛），
 * JSON.stringify 却是递归的（约 1 万层就 RangeError）。也就是说深嵌套负载能顺利
 * 通过 res.json()，却在渲染 <pre> 时抛错——本仓没有 ErrorBoundary，一次抛错卸载
 * 整棵 root = 整页白屏。循环引用同理（fetch 解析结果不会有，但本函数是公共原语）。
 *
 * `?? ''`：JSON.stringify(undefined) / 函数 返回的是 undefined 而非 string，
 * 直接返回会让声明的 `: string` 撒谎。
 */
export const formatJSON = (value: unknown, fallback: string, space: number = 2): string => {
  try {
    return JSON.stringify(value, null, space) ?? '';
  } catch {
    return fallback;
  }
};

/** 归一化丢弃了多少元素（原始数组长度 − 保留数）；非数组输入按 0 计。 */
export const droppedItemCount = (raw: unknown, kept: readonly unknown[]): number =>
  (Array.isArray(raw) ? raw.length : 0) - kept.length;

/** i18next key 片段的保守白名单：仅字母/数字/下划线。 */
const SAFE_KEY_SEGMENT = /^[a-z0-9_]+$/i;

/**
 * 不可信值能否安全地拼进 i18next key。
 *
 * i18next 的 getPath 对最后一段 key 不做 hasOwnProperty 检查，于是原型链上的名字
 * 会穿透到 Object.prototype：`__proto__` 让 t() 返回内部诊断串
 * （"key '…' returned an object instead of string."），`constructor` / `toString`
 * 等让 t() 返回 Function——两者都是 truthy，`|| 原值` 的兜底不会触发，
 * Function 作为 React child 更会被静默丢弃（生产构建无警告）。
 */
export const isSafeKeySegment = (value: string): boolean =>
  SAFE_KEY_SEGMENT.test(value)
  && !Object.prototype.hasOwnProperty.call(Object.prototype, value);

type Translate = (key: string, options: { defaultValue: string }) => unknown;

/**
 * translateSegment 允许的 key 前缀，全量集中在这里。
 *
 * 白名单化只在「前缀下全是字符串叶子」时才成立：若某前缀下新增了嵌套对象，
 * i18next 会返回诊断串 `key '…' returned an object instead of string.`——那是个
 * 非空 string，会被当成译名渲染出去。用常量收口后，任何新增前缀都必须登记到这里，
 * aiClipSafeRender.test.ts 的不变量测试会自动覆盖它（逐个断言叶子全是字符串）。
 */
export const SEGMENT_PREFIXES = {
  shotStatus: 'aiClip.record.shotStatus',
  selectionMode: 'aiClip.record.mode',
  recordCommentStatus: 'aiClip.record.commentStatus',
  warningsCommentStatus: 'aiClip.warnings.commentStatus',
  warningsGate: 'aiClip.warnings.gate',
} as const;

export type SegmentPrefix = typeof SEGMENT_PREFIXES[keyof typeof SEGMENT_PREFIXES];

/**
 * 用不可信值拼动态 i18n key 取译名，取不到就原样输出（经 JSX 转义）。
 *
 * - 不把原值当 defaultValue 交给 i18next：defaultValue 仍跑插值与 $t() 嵌套，
 *   会让外部字符串伪装成本产品文案（如 `$t(aiClip.warnings.none) pwned` → `无警告 pwned`）。
 * - 非白名单片段（含原型链名字）直接跳过 t()。
 * - t() 返回非字符串（Function / object）时同样回落到原值。
 */
export function translateSegment(t: Translate, prefix: SegmentPrefix, value: unknown): string {
  const raw = textOf(value);
  if (!raw || !isSafeKeySegment(raw)) return raw;
  const label = t(`${prefix}.${raw}`, { defaultValue: '' });
  return typeof label === 'string' && label ? label : raw;
}
